import React from "react";
import {
  FileOutlined,
  VideoCameraOutlined,
  AudioOutlined,
  FilePdfOutlined,
  FileWordOutlined,
} from "@ant-design/icons";

export const getFileIcon = (type) => {
  switch (type) {
    case "video":
      return <VideoCameraOutlined className="file-type-icon" />;
    case "audio":
      return <AudioOutlined className="file-type-icon" />;
    case "pdf":
      return <FilePdfOutlined className="file-type-icon" />;
    case "document":
      return <FileWordOutlined className="file-type-icon" />;
    default:
      return <FileOutlined className="file-type-icon" />;
  }
};

export const getFileName = (file) => {
  // Prefer the persisted display_name (clean original filename, post-
  // migration). Fall back to deriving from the url for any pre-migration
  // payload that doesn't carry it, stripping the legacy "<id>_" prefix the
  // upload pipeline used to add for on-disk uniqueness.
  if (file?.display_name) {
    return file.display_name;
  }
  if (file?.url) {
    const base = file.url.split("/").pop() || "";
    const match = base.match(/^\d+_(.+)$/);
    return match ? match[1] : base;
  }
  return `File #${file?.id ?? ""}`;
};

// Australian date format used across the file-manager / SDS / asset-picker
// surfaces. AirAgri is single-region (AU) so locale is fixed rather than
// reading navigator.language — that prevents per-browser drift between
// users on the same property.
//
// fileUtils intentionally uses Intl.DateTimeFormat instead of moment so
// the bare-date short-circuit below is the only TZ logic in the path —
// no moment.utc() / .local() round-trip that needs reasoning about. See
// `commonHelper.displayDateLocal` / `displayDateTimeLocal` for the
// moment-based equivalent used elsewhere in the app.
//
// Why those commonHelper variants aren't reused here:
//   - They do moment.utc(date).local().format(...). Correct for stored
//     UTC datetimes (created_at / updated_at sent by Yii without TZ
//     markers shifted into local AU time).
//   - File expiry_date is a *pure date* (YYYY-MM-DD, no time component).
//     Routing it through moment.utc().local() shifts it forward by the
//     UTC→AEST offset and produces "01/01/2027" for an expiry stored as
//     "2026-12-31" — the exact bug this PR exists to prevent.
//   - These helpers therefore short-circuit the YYYY-MM-DD case before
//     falling back to Intl. They live here (file-browser local) for now
//     to keep the diff scoped; if other surfaces grow the same pure-date
//     need they should move into a shared dateHelper.js alongside
//     commonHelper.
export const AU_DATE_LOCALE = "en-AU";

/**
 * Format an ISO date string / Date as DD/MM/YYYY (AU). Returns "—" for
 * null/undefined and the raw input back when it can't be parsed (so a
 * malformed value is at least visible rather than silently swallowed).
 *
 * IMPORTANT — timezone safety:
 * A bare "YYYY-MM-DD" string fed to new Date() is interpreted as UTC
 * midnight. Calling toLocaleDateString("en-AU") on that then shifts the
 * date forward by ~10–11 hours of TZ offset, so 2026-12-31 ends up
 * rendered as 01/01/2027 for AU users. For pure-date inputs (no time
 * component) we parse the parts directly and skip the Date round-trip.
 */
export const formatDateAu = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "string") {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(AU_DATE_LOCALE, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

/**
 * DD/MM/YYYY HH:mm (24-hour AU). Used for the "Modified" column where we
 * need both date and time; calls toLocaleString with explicit options so
 * the format is deterministic and doesn't depend on browser locale data.
 */
export const formatDateTimeAu = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(AU_DATE_LOCALE, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    // hourCycle 'h23' renders 00–23. hour12: false maps to 'h24' on en-AU
    // in current ICU, which prints midnight as "24:00" — see PR #382 review.
    hourCycle: "h23",
  });
};

/**
 * Compact human-readable size from a byte count. Returns "—" when the
 * size is unknown (legacy rows without size_bytes).
 */
export const formatFileSize = (bytes) => {
  if (bytes === null || bytes === undefined || Number.isNaN(Number(bytes))) {
    return "—";
  }
  const n = Number(bytes);
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = n / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i++;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[i]}`;
};
